import { IPillGroupOption, PillGroup } from '@homex/se-react-components';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import moment from 'moment-timezone';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import { setCurrentCarouselItem, setSelectedTimeSlot } from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { Phrase } from 'components/Phrase';
import { useStringContext } from 'components/StringContext';
import { useAutoAdvanceEffects } from 'hooks/useAutoAdvanceEffects';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useTimeSlotInfoForMonthQuery } from 'hooks/useTimeSlotInfoForMonthQuery';
import {
  CarouselReduxReducer,
  DateTimeFormat,
  IState,
  ITimeSlot,
  ScheduleCalendarRepairCarouselItem,
} from 'typings';
import { formatTimeSlot } from 'utils/formatTimeSlot';
import { tryGetFlowCarouselItem } from 'utils/tryGetFlowCarouselItem';

import {
  AvailabilityDetailsContainer,
  AvailabilityHeading,
  AvailabilityItem,
  AvailabilityMessage,
  Buttons,
  NoteContainer,
  Phone,
  PillContainer,
  Timezone,
} from './styles';

const {
  DATE_COMPARISON: DATE_COMPARISON_FORMAT,
  MONTH_AND_DATE: MONTH_AND_DATE_FORMAT,
  MONTH_COMPARISON: MONTH_COMPARISON_FORMAT,
} = DateTimeFormat;

const userTimezone: string = moment.tz.guess();

export const CAROUSEL_ITEM_ID = CarouselItemId.ScheduleCalendarRepair;

export const CarouselItemScheduleCalendarBottom = () => {
  const dispatch = useThunkDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const strings = useStringContext();
  const {
    addressTimezone = '',
    flow,
    postalCode,
    selectedCalendarDate,
    selectedTimeSlot,
    serviceZoneTimezone,
  } = useSelector((state: IState) => ({
    addressTimezone: state.userInput.address.timezone,
    flow: state.client.flow,
    postalCode: state.userInput.address.zipCode,
    selectedCalendarDate: state.calendar.selectedCalendarDate,
    selectedTimeSlot: state.userInput.selectedTimeSlot,
    serviceZoneTimezone: state.userInput.serviceZoneTimezone,
  }));

  const flowCarouselItem =
    tryGetFlowCarouselItem<ScheduleCalendarRepairCarouselItem>(
      flow,
      CAROUSEL_ITEM_ID,
    );

  const bottomButtonFormat = flow?.buttonType ?? theme.buttonType;

  const { data } = useTimeSlotInfoForMonthQuery();

  const availabilityDetails =
    data?.timeSlotInfoForMonth?.availabilityDetails || [];
  const timeSlots = data?.timeSlotInfoForMonth?.timeSlots || [];

  let legend: React.ReactNode = Phrase({ id: 'SCHEDULE_CALENDAR.LEGEND' });
  let timeSlotsOptionsForSelectedDate: Array<IPillGroupOption> = [];
  let timeSlotsForSelectedDate: Array<ITimeSlot> = [];

  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    const selectedTimeSlotStartTime = event.currentTarget.value;
    const nextSelectedTimeSlot = timeSlotsForSelectedDate.find(
      (slot) => slot.startTime === selectedTimeSlotStartTime,
    );

    if (nextSelectedTimeSlot) {
      dispatch(setSelectedTimeSlot(nextSelectedTimeSlot));
    }
  };

  const handleContinueClick = useCallback(() => {
    let destinationItemId;

    if (flowCarouselItem) {
      destinationItemId = flowCarouselItem.options.destinations.next;
    } else {
      destinationItemId =
        theme.enterpriseEnabled && theme.enterpriseVersion === '2'
          ? CarouselItemId.AvailablePartners
          : CarouselItemId.RepairSummary;
    }

    dispatch(
      setCurrentCarouselItem(CarouselReduxReducer.SERVICE, destinationItemId),
    );
  }, [
    dispatch,
    flowCarouselItem,
    theme.enterpriseEnabled,
    theme.enterpriseVersion,
  ]);

  const selectedTimeSlotStartTime = selectedTimeSlot?.startTime || '';
  const isTimezoneVisible =
    addressTimezone &&
    (addressTimezone !== serviceZoneTimezone ||
      addressTimezone !== userTimezone);
  const timezoneAbbreviation = moment.tz(addressTimezone).zoneAbbr();

  if (timeSlots.length > 0 && selectedCalendarDate) {
    const selectedDate = moment(selectedCalendarDate, DATE_COMPARISON_FORMAT);
    const selectedDay = selectedDate.date();
    const selectedMonth = selectedDate.format(MONTH_COMPARISON_FORMAT);

    const currentMonth = moment
      .tz(timeSlots[0]?.startTime, addressTimezone)
      .format(MONTH_COMPARISON_FORMAT);

    if (selectedMonth === currentMonth) {
      legend = selectedDate.format(DateTimeFormat.DATE_HEADER);

      timeSlotsForSelectedDate = timeSlots.filter(
        ({ startTime }) =>
          moment.tz(startTime, addressTimezone).date() === selectedDay,
      );

      timeSlotsOptionsForSelectedDate = timeSlotsForSelectedDate.map(
        (timeSlot) => {
          const { startTime } = timeSlot;

          return {
            key: startTime,
            label: formatTimeSlot(timeSlot, addressTimezone),
            value: startTime,
          };
        },
      );
    }
  }

  const hasTimeSlots = timeSlots.length > 0;

  if (!hasTimeSlots) {
    legend = Phrase({ id: 'SCHEDULE_CALENDAR.NO_TIMESLOTS' });
  }

  const isNoteContainerVisible =
    isTimezoneVisible || theme.calendarPhoneLineEnabled;

  const startTime = selectedTimeSlot?.startTime;

  // allow flows that get postalCode before customer to proceed
  const timeSlotDay =
    (postalCode || addressTimezone) &&
    moment.tz(startTime, addressTimezone).format(DATE_COMPARISON_FORMAT);

  const isContinueButtonEnabled =
    timeSlotDay === selectedCalendarDate && !!selectedTimeSlot;

  const usedCalendarPhoneLineEnabled = flowCarouselItem
    ? flowCarouselItem.options.calendarPhoneLineEnabled
    : theme.calendarPhoneLineEnabled;

  useAutoAdvanceEffects(
    isContinueButtonEnabled,
    useCallback(() => {
      setIsLoading(true);
    }, []),
    useCallback(() => {
      setIsLoading(false);
      handleContinueClick();
    }, [handleContinueClick]),
  );

  return (
    <>
      {availabilityDetails.length > 0 && (
        <AvailabilityDetailsContainer>
          {availabilityDetails.map(({ end, id, message, start }) => {
            const availabilityDetailsDates = [
              moment.tz(start, addressTimezone).format(MONTH_AND_DATE_FORMAT),
              end
                ? moment.tz(end, addressTimezone).format(MONTH_AND_DATE_FORMAT)
                : null,
            ].filter((availabilityDetailsDate) => !!availabilityDetailsDate);

            return (
              <AvailabilityItem key={id}>
                <AvailabilityHeading>
                  {availabilityDetailsDates.join(' - ')}
                </AvailabilityHeading>
                <AvailabilityMessage>{message}</AvailabilityMessage>
              </AvailabilityItem>
            );
          })}
        </AvailabilityDetailsContainer>
      )}
      <PillContainer>
        <PillGroup
          onChange={handleChange}
          options={timeSlotsOptionsForSelectedDate}
          title={legend}
          value={selectedTimeSlotStartTime}
        />
      </PillContainer>
      {isNoteContainerVisible && (
        <NoteContainer>
          {isTimezoneVisible && (
            <Timezone>
              <Phrase
                id="SCHEDULE_CALENDAR.TIMEZONE.TEMPLATE"
                TIME_ZONE_LONG={
                  strings[`TIME_ZONE_LONG.${timezoneAbbreviation}`] ?? ''
                }
                TIME_ZONE_SHORT={timezoneAbbreviation}
              />
            </Timezone>
          )}
          {usedCalendarPhoneLineEnabled && (
            <Phone>
              <Phrase id="SCHEDULE_CALENDAR.PHONE.HTML" />
            </Phone>
          )}
        </NoteContainer>
      )}
      <Buttons>
        <CarouselBottomButton
          buttonFormat={bottomButtonFormat}
          carouselItemId={CAROUSEL_ITEM_ID}
          isDisabled={!isContinueButtonEnabled}
          isLoading={isLoading}
          onContinueClick={handleContinueClick}
        />
      </Buttons>
    </>
  );
};
